import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Layout, Breadcrumb, Spin, message } from 'antd'
import { FileTextOutlined, StarOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'

// Components
import MainLayout from '../../components/mainLayout'
import KpiCard from 'components/kpiCard'
import UniversalBarChart from 'components/charts/UniversalBarChart'
import UniversalPieChart from 'components/charts/UniversalPieChart'
import { TOP_5_COLORS, TYPE_COLORS } from 'components/charts/chartColors'
import { FilterButton, FilterSidebar } from 'components/filterButton'
import MetabaseDashboard from './metabaseDashboard'

// Hooks
import { useMetabaseCheck } from 'hooks/dashboard/useMetabaseCheck'
import useFilters from 'components/filterButton/useFilters'

// API
import serviceAPI from 'services/service_api'
import {
	getDocumentTemplatesOptions,
	getDocumentUsersOptions,
	getDocumentWorkflowStagesOptions,
	getDashboardStats,
	getDashboardArchivedCount,
	getDashboardByTemplate,
	getDashboardByUser,
	getDashboardWorkflowStages,
} from '../../services/documents/documentDashboardServices'

// CSS
import './styles.scss'

function DocumentDashboard() {
	const history = useHistory()
	const [stats, setStats] = useState(null)
	const [archivedCount, setArchivedCount] = useState(0)
	const [byTemplate, setByTemplate] = useState([])
	const [byUser, setByUser] = useState([])
	const [workflowStagesData, setWorkflowStagesData] = useState([])
	const [customData, setCustomData] = useState(null)

	const [loaders, setLoaders] = useState({
		stats: true,
		archived: true,
		template: true,
		user: true,
		workflow: true,
	})

	const noticePeriodColumns = [
		{ title: 'Prazo de Aviso', dataIndex: 'name', key: 'name' },
		{ title: 'Quantidade de Contratos', dataIndex: 'value', key: 'value' },
	]

	// Arrumar depois
	const { hasMetabase } = useMetabaseCheck()

	const { filters, setFilter } = useFilters({
		dateRange: [],
		template: [],
		user: [],
		workflowStages: [],
	})

	useEffect(() => {
		let isMounted = true
		const getDashboardData = () => {
			setLoaders({
				stats: true,
				archived: true,
				template: true,
				user: true,
				workflow: true,
			})

			const startDate = filters.dateRange?.[0]
				? dayjs(filters.dateRange[0]).toISOString()
				: null
			const endDate = filters.dateRange?.[1]
				? dayjs(filters.dateRange[1]).toISOString()
				: null
			const expStartDate = filters.expirationDate?.[0]
				? dayjs(filters.expirationDate[0]).toISOString()
				: null
			const expEndDate = filters.expirationDate?.[1]
				? dayjs(filters.expirationDate[1]).toISOString()
				: null

			const payloadFilters = {
				start_date: startDate,
				end_date: endDate,
				expiration_start_date: expStartDate,
				expiration_end_date: expEndDate,
				templates: filters.template,
				users: filters.user,
				workflow_stages: filters.workflowStages,
			}

			getDashboardStats(payloadFilters)
				.then((res) => {
					if (!isMounted) return
					const statsData = res?.data || res || {}
					setStats(statsData)
					setCustomData(statsData?.custom_data || null)
				})
				.catch((e) => console.error('Erro no stats:', e))
				.finally(
					() => isMounted && setLoaders((prev) => ({ ...prev, stats: false }))
				)

			getDashboardArchivedCount(payloadFilters)
				.then((res) => {
					if (!isMounted) return
					setArchivedCount(res?.data?.count || res?.count || 0)
				})
				.catch((e) => console.error('Erro no archived:', e))
				.finally(
					() =>
						isMounted && setLoaders((prev) => ({ ...prev, archived: false }))
				)

			getDashboardByTemplate(payloadFilters)
				.then((res) => {
					if (!isMounted) return
					const data = res?.data || res
					setByTemplate(Array.isArray(data) ? data : [])
				})
				.catch((e) => console.error('Erro no template:', e))
				.finally(
					() =>
						isMounted && setLoaders((prev) => ({ ...prev, template: false }))
				)

			getDashboardByUser(payloadFilters)
				.then((res) => {
					if (!isMounted) return
					const data = res?.data || res
					setByUser(Array.isArray(data) ? data : [])
				})
				.catch((e) => console.error('Erro no user:', e))
				.finally(
					() => isMounted && setLoaders((prev) => ({ ...prev, user: false }))
				)

			getDashboardWorkflowStages(payloadFilters)
				.then((res) => {
					if (!isMounted) return
					const workflow = res?.data || res
					if (Array.isArray(workflow)) {
						setWorkflowStagesData(
							workflow.filter(
								(item) => item.value > 0 && item.name !== 'Sem Status'
							)
						)
					}
				})
				.catch((e) => console.error('Erro no workflow:', e))
				.finally(
					() =>
						isMounted && setLoaders((prev) => ({ ...prev, workflow: false }))
				)
		}

		getDashboardData()

		return () => {
			isMounted = false
		}
	}, [filters])

	const renderSupersimContent = () => {
		if (!customData || !customData.kpis) return null

		const activeInactiveData = [
			{ name: 'Ativos', value: customData.kpis.total_ativos || 0 },
			{ name: 'Inativos', value: customData.kpis.total_inativos || 0 },
		]

		const lgpdData = [
			{ name: 'Com LGPD', value: customData.kpis.lgpd_sim || 0 },
			{ name: 'Sem LGPD', value: customData.kpis.lgpd_nao || 0 },
		]

		const multaData = [
			{ name: 'Com Multa', value: customData.kpis.multa_sim || 0 },
			{ name: 'Sem Multa', value: customData.kpis.multa_nao || 0 },
		]

		const hasAvisoPrevio =
			customData.charts.aviso_previo &&
			customData.charts.aviso_previo.length > 0

		return (
			<div className="supersim-section" style={{ marginTop: '30px' }}>
				<h2
					style={{
						marginBottom: '20px',
						borderBottom: '2px solid #f0f0f0',
						paddingBottom: '10px',
					}}>
					Análise Estratégica Customizada
				</h2>

				<div
					style={{
						display: 'grid',
						gridTemplateColumns: '1fr 1fr',
						gap: '20px',
						marginBottom: '20px',
					}}>
					<UniversalPieChart
						title="Status dos Contratos (Ativos vs Inativos)"
						data={activeInactiveData}
						colors={['#52c41a', '#f5222d']}
					/>
					<UniversalBarChart
						title="Prazo de Vigência"
						data={customData.charts.vigencia || []}
						yKey="value"
						colors={TOP_5_COLORS}
					/>
				</div>

				<div
					style={{
						display: 'grid',
						gridTemplateColumns: '1fr 1fr',
						gap: '20px',
						marginBottom: '20px',
					}}>
					<UniversalPieChart
						title="Presença de Cláusula LGPD"
						data={lgpdData}
						colors={['#1890ff', '#a4d3ff']}
					/>
					<UniversalPieChart
						title="Presença de Cláusula de Multa"
						data={multaData}
						colors={['#722ed1', '#c4a0f6']}
					/>
				</div>

				<div
					style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
					{hasAvisoPrevio && (
						<div style={{ marginBottom: '20px' }}>
							<UniversalPieChart
								title="Prazos de Aviso Prévio"
								data={customData.charts.aviso_previo}
								colors={TOP_5_COLORS}
							/>
						</div>
					)}
					<UniversalBarChart
						title="Principais Motivos de Multa"
						data={customData.charts.motivos_multa || []}
						yKey="value"
						layout="vertical"
						colors={TYPE_COLORS}
					/>
				</div>
			</div>
		)
	}

	const renderNubankContent = () => {
		if (!customData || customData.cliente !== 'Nubank') return null

		const kpiColumns = customData ? 'repeat(3, 1fr)' : 'repeat(3, 1fr)'

		return (
			<div className="nubank-section" style={{ marginTop: '30px' }}>
				<h2
					style={{
						marginBottom: '20px',
						borderBottom: '2px solid #f0f0f0',
						paddingBottom: '10px',
					}}>
					SLA e Eficiência do Workflow
				</h2>

				<div
					className="kpi-row"
					style={{
						gridTemplateColumns: kpiColumns,
						gap: '20px',
						marginBottom: '20px',
					}}>
					<KpiCard
						title="Tempo Médio de Assinatura (E2E)"
						value={customData.slas?.tempo_medio_assinatura || '00h 00m'}
					/>
					<KpiCard
						title="Tempo Médio de Aprovação do Diretor"
						value={customData.slas?.tempo_medio_aprovacao_diretor || '00h 00m'}
					/>
					<KpiCard
						title="Tempo Médio de Aprovação do Jurídico"
						value={customData.slas?.tempo_medio_aprovacao_juridico || '00h 00m'}
					/>
				</div>
			</div>
		)
	}

	// if (hasMetabase) return <MetabaseDashboard />

	const renderFilter = (
		<FilterSidebar>
			<FilterButton
				title="Período (Criação)"
				filterKey="date_range"
				currentSection="documents"
				type="date"
				isPopover={false}
				selected={filters.dateRange}
				onChange={(val) => {
					setFilter('dateRange', val)
				}}
			/>
			<FilterButton
				title="Vencimento"
				filterKey="expiration_date"
				currentSection="documents"
				type="date"
				isPopover={false}
				selected={filters.expirationDate}
				onChange={(val) => setFilter('expirationDate', val)}
			/>
			<FilterButton
				title="Modelos"
				filterKey="template"
				currentSection="documents"
				type="checkbox"
				isPopover={false}
				hasAsyncSearch={true}
				asyncFetch={getDocumentTemplatesOptions}
				selected={filters.template}
				onChange={(val) => setFilter('template', val)}
			/>
			<FilterButton
				title="Etapa Workflow"
				filterKey="workflow_stages"
				currentSection="documents"
				type="checkbox"
				isPopover={false}
				hasAsyncSearch={true}
				asyncFetch={getDocumentWorkflowStagesOptions}
				selected={filters.workflowStages}
				onChange={(val) => setFilter('workflowStages', val)}
			/>
			<FilterButton
				title="Usuários"
				filterKey="user"
				currentSection="documents"
				type="checkbox"
				isPopover={false}
				hasAsyncSearch={true}
				asyncFetch={getDocumentUsersOptions}
				selected={filters.user}
				onChange={(val) => setFilter('user', val)}
			/>
		</FilterSidebar>
	)

	const renderContent = () => {
		const kpiColumns = customData?.kpis ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)'

		return (
			<div className="dashboard-container" style={{ padding: '24px' }}>
				<Spin spinning={loaders.stats || loaders.archived}>
					<div
						className="kpi-row"
						style={{
							gridTemplateColumns: kpiColumns,
							gap: '20px',
							marginBottom: '20px',
						}}>
						<KpiCard
							title="Solicitados"
							value={stats?.kpis?.total_documents || 0}
						/>
						<KpiCard
							title="Assinados"
							value={stats?.kpis?.total_signed || 0}
							icon={<FileTextOutlined />}
						/>
						<KpiCard title="Arquivados" value={archivedCount} />
						{customData?.kpis && (
							<KpiCard
								title="Contratos Estratégicos"
								value={customData?.kpis?.total_estrategicos || 0}
								icon={<StarOutlined style={{ color: '#fadb14' }} />}
							/>
						)}
					</div>
				</Spin>

				<Spin spinning={loaders.stats}>
					{renderSupersimContent()}
					{renderNubankContent()}
				</Spin>

				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(2, 1fr)',
						gap: '20px',
						marginTop: '20px',
					}}>
					<Spin spinning={loaders.workflow}>
						<UniversalBarChart
							title="Documentos por Workflow"
							data={workflowStagesData}
							yKey="value"
							colors={TOP_5_COLORS}
							emptyMessage="Nenhum dado de workflow."
						/>
					</Spin>

					<Spin spinning={loaders.template}>
						<UniversalBarChart
							title="Documentos por Modelo"
							data={byTemplate}
							yKey="value"
							colors={TOP_5_COLORS}
							emptyMessage="Nenhum modelo utilizado."
						/>
					</Spin>

					<div style={{ gridColumn: 'span 2' }}>
						<Spin spinning={loaders.user}>
							<UniversalBarChart
								title="Documentos por Usuários"
								data={byUser}
								yKey="value"
								colors={TYPE_COLORS}
								emptyMessage="Nenhum usuário encontrado."
							/>
						</Spin>
					</div>
				</div>
			</div>
		)
	}

	return (
		<MainLayout selectedKey={'dashboard'}>
			<Layout style={{ backgroundColor: '#fff', minHeight: '100vh' }}>
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						padding: '16px 24px',
						background: '#fff',
						borderBottom: '1px solid #f0f0f0',
					}}>
					<Breadcrumb>
						<Breadcrumb.Item
							onClick={() => history.push('/documents')}
							style={{ cursor: 'pointer' }}>
							Documentos
						</Breadcrumb.Item>
						<Breadcrumb.Item>Dashboard</Breadcrumb.Item>
					</Breadcrumb>

					<div className="filter-container">{renderFilter}</div>
				</div>

				{renderContent()}
			</Layout>
		</MainLayout>
	)
}

export default DocumentDashboard
